import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation.js';
import * as utils from '../common/Utils.js'
import { useConfig } from '../contexts/ConfigContext.js';
import { useAdminGuard } from '../hooks/useAdminGuard.js';
import { useLeagueData } from '../contexts/LeagueDataContext.js';

import { collection, getDoc, getDocs, setDoc, updateDoc, doc, Timestamp, serverTimestamp } from "firebase/firestore";
import { db } from '../firebase.js';
let SAVE_BLOCKS_AND_STEALS = true;

const AdminGames = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const { currentUser, isReady } = useAdminGuard();
  const { teams: contextTeams, seasonTeams: contextSeasonTeams, players: contextPlayers, games: contextGames, dataLoaded, refreshLeagueData } = useLeagueData(['teams', 'seasonTeams', 'players', 'games']);
  const SEASON = config.currentSeason;
  const { gameID } = useParams();
  const [games, setGames] = useState([]);
  const [players, setPlayers] = useState([])
  const [teams, setTeams] = useState([])
  const [seasonTeams, setSeasonTeams] = useState([])
  const [gameStats, setGameStats] = useState({})
  const [dataReady, setDataReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStep, setSaveStep] = useState('');
  const [saveProgress, setSaveProgress] = useState(0);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [articleStatus, setArticleStatus] = useState('idle'); // 'idle' | 'generating' | 'done' | 'error'
  const [savedTalkingPoints, setSavedTalkingPoints] = useState('');
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);
  const [game, setGame] = useState({})
  const [awayTeam, setAwayTeam] = useState({
    id: null,
    name: game.awayTeamPlaceholder || 'TBD',
    players: [],
    seed: ''
  })
  const [homeTeam, setHomeTeam] = useState({
    id: null,
    name: game.homeTeamPlaceholder || 'TBD',
    players: [],
    seed: ''
  })

  useEffect(() => {
    if (!isReady || !dataLoaded) return;

    getLeague();
  }, [isReady, dataLoaded]); // eslint-disable-line react-hooks/exhaustive-deps



  const getLeague = async () => {
    setPlayers(contextPlayers)
    setGames(contextGames)
    setTeams(contextTeams)

    if (gameID) {
      let game = utils.getGameForID(contextGames, gameID)
      setGame(game)

      let thisSeasonTeamData = contextSeasonTeams.filter(st => st.season == game.season);

      const teams = thisSeasonTeamData.map(st => { {
        const team = contextTeams.find(t => t.id === st.teamID);
        return {
          ...st,
          ...team
        };
      }})

      setSeasonTeams(teams);

      const showPlaceholders = game.season == SEASON && game.type == 'playoff' && !config.showPlayoffBracket

      if (!showPlaceholders) {
        if (game.awayTeamID) {
          let awayTeam = utils.getTeamForID(teams, game.awayTeamID);
          setAwayTeam(awayTeam)
        }
        if (game.homeTeamID) {
          let homeTeam = utils.getTeamForID(teams, game.homeTeamID);
          setHomeTeam(homeTeam)
        }
      }

      const [articleSnap, gameStatSnap] = await Promise.all([
        getDoc(doc(db, 'articles', gameID)),
        getDoc(doc(db, 'gameStats', gameID))
      ]);

      if (articleSnap.exists()) {
        setSavedTalkingPoints(articleSnap.data().talkingPoints || '');
      }

      const stats = (gameStatSnap.exists() ? gameStatSnap.data().stats : []) || [];
      setGameStats({ [gameID]: stats.map((stat) => ({ ...stat, gameID })) });
    }

    setDataReady(true);
  }

  const fetchAllGameStats = async () => {
    const gameStatsSnapshot = await getDocs(collection(db, "gameStats"));
    const allGameStats = [];
    gameStatsSnapshot.forEach(doc => {
      const gameID = doc.id
      const stats = (doc.data().stats || []).map((stat) => ({ ...stat, gameID }))
      allGameStats.push(...stats);
    });
    return allGameStats;
  }

  function displayFinishedGameThisWeek (game) {
    const away = utils.getTeamForID(teams, game.awayTeamID) || { name: 'TBD' };
    const home = utils.getTeamForID(teams, game.homeTeamID) || { name: 'TBD' };

    if (game.status === 'completed') {
      return (
        <div className="league-game">
          <div className='league-game-info'>
            <p className="league-title-standard-small" key={game.id + "-date"}>{ game.date.toDateString() }</p>
            { game.awayTeamID === game.winner && <p key={game.id}><b>{ away.name.toUpperCase() } { game.score.away }</b> - { game.score.home } { home.name.toUpperCase() }</p> }
            { game.homeTeamID === game.winner && <p key={game.id}>{ away.name.toUpperCase() } { game.score.away } - <b>{ game.score.home } { home.name.toUpperCase() }</b></p> }
          </div>
          <div className='league-game-edit-button'>
            <a type="button" className="btn btn-dark" href={'/admin/games/' + game.id}>Edit</a>
          </div>
        </div>
      )
    } else {
      return (
        <div className="league-game">
          <div className='league-game-info'>
            <p className="league-title-standard-small" key={game.id + "-date"}>{ game.date.toDateString() }</p>
            <p key={game.id}>Score Pending - { away.name.toUpperCase() } vs. { home.name.toUpperCase() }</p>
          </div>
          <div className='league-game-edit-button'>
            <a type="button" className="btn btn-dark" href={'/admin/games/' + game.id}>Edit</a>
          </div>
        </div>
      )
    }
  }

  function displayGameThisWeek (game) {
    const away = utils.getTeamForID(teams, game.awayTeamID) || { name: 'TBD' };
    const home = utils.getTeamForID(teams, game.homeTeamID) || { name: 'TBD' };

    return (
      <div className="league-game" key={game.id + '-game'}>
        <div className='league-game-info'>
          <p className="league-title-skinny-small" key={game.id + "-date"}>{ game.date.toDateString() }</p>
          <p key={game.id}>{ away.name.toUpperCase() } vs. { home.name.toUpperCase() }</p>
        </div>
        <div className='league-game-edit-button'>
          <a type="button" className="btn btn-dark" href={'/admin/games/' + game.id}>Edit</a>
        </div>
      </div>
    )
  }

  function displayGames () {
    const today = Date.now();

    const futureGames = games.filter((game) => game.season == SEASON && game.status !== 'completed' && game.date > today && (game.awayTeamID !== '' && game.homeTeamID !== ''))
    const pastGames = games.filter((game) => game.season == SEASON && (game.date <= today || game.status === 'completed') )

    const sortedPastGames = pastGames.sort((a, b) => (a.date < b.date) ? -1 : 1)
    const sortedFutureGames = futureGames.sort((a, b) => (a.date < b.date) ? -1 : 1)

    const pastGameElements = sortedPastGames.map((game, place) => displayFinishedGameThisWeek(game) )
    const futureGameElements = sortedFutureGames.map((game, place) => displayGameThisWeek(game) )
    return (
      <>
        { pastGameElements }
        { futureGameElements }
      </>
    );
  }

  function displayPlayerInputRowsForTeam(game, player) {
    // abstract this
    let statsForThisGame = gameStats[game.id] || [];
    let playerStat = statsForThisGame.find((stat) => stat.playerID == player.id)

    if (!playerStat || ['dnp', 'forfeit', 'no-stats'].includes(playerStat.status)) {
      playerStat = {
        points: '',
        rebounds: '',
        assists: '',
        steals: '',
        blocks: ''
      }
    }

    return (
      <>
        <tr>
          <th className="league-table-data-name">{ utils.displayJerseyNumber(player) } { utils.displayPlayerName(player) }</th>
          <th className="league-table-data-name full-input">
            <input id={ getIDForPlayerAndStat(player, 'points') } type="number" className="form-control" defaultValue={ playerStat.points }></input>
          </th>
          <th className="league-table-data-name full-input">
            <input id={ getIDForPlayerAndStat(player, 'rebounds') } type="number" className="form-control" defaultValue={ playerStat.rebounds }></input>
          </th>
          <th className="league-table-data-name full-input">
            <input id={ getIDForPlayerAndStat(player, 'assists') } type="number" className="form-control" defaultValue={ playerStat.assists }></input>
          </th>
          { SAVE_BLOCKS_AND_STEALS == false ? '' :
            <>
              <th className="league-table-data-name full-input">
                <input id={ getIDForPlayerAndStat(player, 'steals') } type="number" className="form-control" defaultValue={ playerStat.steals }></input>
              </th>
              <th className="league-table-data-name full-input">
                <input id={ getIDForPlayerAndStat(player, 'blocks') } type="number" className="form-control" defaultValue={ playerStat.blocks }></input>
              </th>
            </>
          }
        </tr>
      </>
    )
  }

  function displayPlayerInputRows(game) {
    let awayTeamPlayers = awayTeam.players.map((id) => utils.getPlayerByID(players, id) || { id, firstName: id, lastName: '' }).sort((a, b) => jerseyNumberSort(a,b))
    let awayTeamPlayerRows = awayTeamPlayers.map((player, place) => displayPlayerInputRowsForTeam(game, player))

    let homeTeamPlayers = homeTeam.players.map((id) => utils.getPlayerByID(players, id) || { id, firstName: id, lastName: '' }).sort((a, b) => jerseyNumberSort(a,b))
    let homeTeamPlayerRows = homeTeamPlayers.map((player, place) => displayPlayerInputRowsForTeam(game, player))

    return (
      <>
        <tr>
          <th className="league-table-heading-name" colSpan="100%"><span>{ awayTeam.name.toUpperCase() }</span></th>
        </tr>
        { awayTeamPlayerRows }
        <tr>
          <th className="league-table-heading-name" colSpan="100%"><span>{ homeTeam.name.toUpperCase() }</span></th>
        </tr>
        { homeTeamPlayerRows }
      </>
    )
  }

  function jerseyNumberSort (a, b) {
    if (a.jerseyNumber == '' && b.jerseyNumber == '') {
      return 0;
    } else if (a.jerseyNumber == '') {
      return 1;
    } else if (b.jerseyNumber == '') {
      return -1;
    } else {
      let number1 = parseInt(a.jerseyNumber)
      let number2 = parseInt(b.jerseyNumber)

      return number1 < number2 ? -1 : 1
    }
  }

  const saveGameData = async () => {
    let game = utils.getGameForID(games, gameID);
    let awayTeam = utils.getTeamForID(seasonTeams, game.awayTeamID)
    let homeTeam = utils.getTeamForID(seasonTeams, game.homeTeamID)

    let awayFinalScore = parseInt(document.getElementById('finalScoreAwayTeam').value);
    let homeFinalScore = parseInt(document.getElementById('finalScoreHomeTeam').value);
    let gameWithNoStats = document.getElementById('noStatsCheckbox').checked;
    let gameWithForfeit = document.getElementById('forfeitCheckbox').checked;
    let gameWithOvertime = document.getElementById('overtimeCheckbox').checked;
    let rewriteArticleCheckbox = document.getElementById('rewriteArticleCheckbox').checked;
    let talkingPoints = document.getElementById('articleTalkingPoints').value.trim();

    game.score.away = awayFinalScore;
    game.score.home = homeFinalScore;
    game.winner = awayFinalScore > homeFinalScore ? awayTeam.id : homeTeam.id;
    game.status = 'completed';
    game.overtime = gameWithOvertime

    let stats = [];

    let gamePlayerIDs = [...awayTeam.players, ...homeTeam.players]
    let gamePlayers = gamePlayerIDs.map((playerID) => utils.getPlayerByID(players, playerID))
    gamePlayers.forEach((player) => {
      let playerTeam = utils.getCurrentTeamForPlayerID(seasonTeams, player.id)
      let points = parseInt(document.getElementById(getIDForPlayerAndStat(player, 'points')).value);
      let rebounds = parseInt(document.getElementById(getIDForPlayerAndStat(player, 'rebounds')).value);
      let assists = parseInt(document.getElementById(getIDForPlayerAndStat(player, 'assists')).value);
      let steals;
      let blocks;
      if (SAVE_BLOCKS_AND_STEALS) {
        steals = parseInt(document.getElementById(getIDForPlayerAndStat(player, 'steals')).value);
        blocks = parseInt(document.getElementById(getIDForPlayerAndStat(player, 'blocks')).value);
      }

      let gameStat = {
        gameID: game.id,
        playerID: player.id,
        teamID: playerTeam.id
      }
      if (SAVE_BLOCKS_AND_STEALS) {
        if (isNaN(points) && isNaN(rebounds) && isNaN(assists) && isNaN(blocks) && isNaN(steals)) {
          gameStat.points = 0
          gameStat.rebounds = 0
          gameStat.assists = 0
          gameStat.steals = 0
          gameStat.blocks = 0
          if (gameWithForfeit) {
            gameStat.status = 'forfeit'
          } else if (gameWithNoStats) {
            gameStat.status = 'no-stats'
          } else {
            gameStat.status = 'dnp'
          }
        } else {
          points = points || 0
          rebounds = rebounds || 0
          assists = assists || 0
          steals = steals || 0
          blocks = blocks || 0

          gameStat.points = points
          gameStat.rebounds = rebounds
          gameStat.assists = assists
          gameStat.steals = steals
          gameStat.blocks = blocks
          gameStat.status = 'played'
        }
      } else {
        if (isNaN(points) && isNaN(rebounds) && isNaN(assists)) {
          gameStat.points = 0
          gameStat.rebounds = 0
          gameStat.assists = 0
          if (gameWithForfeit) {
            gameStat.status = 'forfeit'
          } else if (gameWithNoStats) {
            gameStat.status = 'no-stats'
          } else {
            gameStat.status = 'dnp'
          }
        } else {
          points = points || 0
          rebounds = rebounds || 0
          assists = assists || 0

          gameStat.points = points
          gameStat.rebounds = rebounds
          gameStat.assists = assists
          gameStat.status = 'played'
        }
      }
      stats.push(gameStat)
    })

    // fetch all game stats fresh (only needed at save time) and merge in this game's new stats
    const allGameStats = await fetchAllGameStats();
    stats.forEach((stat) => {
      let existingStatIndex = allGameStats.findIndex(
        (s) => s.gameID == stat.gameID && s.playerID == stat.playerID
      )

      if (existingStatIndex >= 0) {
        allGameStats[existingStatIndex] = stat
      } else {
        allGameStats.push(stat)
      }
    })


    let updatedGames = games.map((g) => g.id === gameID ? game : g)

    await setGames(updatedGames)

    // if it's a playoff game, advance team to next game

    if (game.type === 'playoff') {
      let gameWinnerID = `winner_${game.id}`

      let nextGame = updatedGames.find((game) => game.type == 'playoff' && game.playoffTags.includes(gameWinnerID))
      if (nextGame) {
        const winners = nextGame.playoffTags.filter((tag) => tag.startsWith('winner_'))
        if (winners.length === 1) { // set to home team for game
          nextGame.homeTeamID = game.winner;
        } else if (winners.length === 2){
          if (winners[0] === gameWinnerID) { // set to away team for game
            nextGame.awayTeamID = game.winner;
          } else { // set to home team for game
            nextGame.homeTeamID = game.winner;
          }
        }

        await updateGame(nextGame)
      }
    }

    setIsSaving(true);

    setSaveStep('Saving game…'); setSaveProgress(10);
    await Promise.all([saveGame(game), saveSeasonTeams(), saveSeasonLeaders(allGameStats), saveSeasonUpcomingGames(updatedGames)]);

    setSaveStep('Saving player stats…'); setSaveProgress(35);
    await saveStatsForGame(game, stats);

    setSaveStep('Updating records…'); setSaveProgress(60);
    await updateRecords(game, stats);

    setSaveStep('Saving player views…'); setSaveProgress(75);
    await savePlayerViews(game, stats, allGameStats);

    setSaveStep('Refreshing data…'); setSaveProgress(90);
    await refreshLeagueData();

    setSaveStep('Done!'); setSaveProgress(100);
    setIsSaving(false);

    const canCreateArticle = !gameWithNoStats && !gameWithForfeit;
    const existingArticleSnap = canCreateArticle ? await getDoc(doc(db, 'articles', game.id)) : null;
    const willGenerate = canCreateArticle && (!existingArticleSnap?.exists() || rewriteArticleCheckbox);

    if (willGenerate) {
      setShowArticleModal(true);
      setArticleStatus('generating');
      writeAndSaveArticle(game, stats, rewriteArticleCheckbox, true, talkingPoints)
        .then(() => { if (isMountedRef.current) setArticleStatus('done'); })
        .catch(() => { if (isMountedRef.current) setArticleStatus('error'); });
    } else {
      writeAndSaveArticle(game, stats, false, canCreateArticle, talkingPoints);
      navigate(`/games/${gameID}`);
    }
  }

  async function updateArticleMetadataInUpcomingGames(season) {
    const seasonRef = doc(db, 'seasons', String(season));
    const seasonSnap = await getDoc(seasonRef);
    if (!seasonSnap.exists()) return;

    const rawUpcomingGames = seasonSnap.data().upcomingGames || [];
    const completedGames = rawUpcomingGames.filter(g => g.status === 'completed');
    const articleSnaps = await Promise.all(completedGames.map(g => getDoc(doc(db, 'articles', g.id))));

    const articleMap = {};
    articleSnaps.forEach((snap, i) => {
      if (snap.exists()) {
        const { title, summary, created_at } = snap.data();
        articleMap[completedGames[i].id] = { articleTitle: title, articleSummary: summary, articleCreatedAt: created_at };
      }
    });

    const upcomingGames = rawUpcomingGames.map(g =>
      articleMap[g.id] ? { ...g, ...articleMap[g.id] } : g
    );
    await updateDoc(seasonRef, { upcomingGames, updatedAt: serverTimestamp() });
  }

  async function writeAndSaveArticle (game, stats, rewriteArticle = false, allowCreate = true, talkingPoints = '') {
    const articleRef = doc(db, 'articles', game.id);
    const articleSnap = await getDoc(articleRef);

    if (articleSnap.exists() && !rewriteArticle) {
      await Promise.all([
        updateArticleMetadataInUpcomingGames(game.season),
        updateDoc(articleRef, { talkingPoints }),
      ]);
      return;
    }

    if (!allowCreate) return;

    const awayTeam = seasonTeams.find((t) => t.id == game.awayTeamID)
    const homeTeam = seasonTeams.find((t) => t.id == game.homeTeamID)
    const gameType = game.type == 'playoff' ? 'Playoff Game' : 'Regular Season Game'

    const playedStats = stats
      .filter((s) => s.status === 'played')
      .map((s) => {
        const p = utils.getPlayerByID(players, s.playerID);
        return {
          ...s,
          playerName: utils.displayPlayerName(p),
          height: utils.displayPlayerHeight(p)
        };
      });

    const gameLocationMap = {
      'b&gc': "Boys and Girls Club",
      'sbca': "South Boston Catholic Academy",
      'jpt-school': "Joseph P. Tynan School",
      'condon': "Condon School"
    }

    const article = {
      matchup: `${awayTeam.name} vs. ${homeTeam.name}`,
      date: game.date.toString(),
      location: gameLocationMap[game.location],
      score: `${awayTeam.name} ${game.score.away} - ${game.score.home} ${homeTeam.name}`,
      gameType: gameType,
      overtimeGame: game?.overtime || false,
      teamDataAway: {
        name: awayTeam.name,
        record: `${awayTeam.stats.wins}-${awayTeam.stats.losses}`,
        streak: awayTeam.stats.streak >= 0 ? `${awayTeam.stats.streak}W` : `${Math.abs(awayTeam.stats.streak)}L`,
        pointDifferential: awayTeam.stats.pointsFor - awayTeam.stats.pointsAgainst,
        gamesPlayed: awayTeam.stats.games,
        playerStats: playedStats.filter((s) => s.teamID == game.awayTeamID)
      },
      teamDataHome: {
        name: homeTeam.name,
        record: `${homeTeam.stats.wins}-${homeTeam.stats.losses}`,
        streak: homeTeam.stats.streak >= 0 ? `${homeTeam.stats.streak}W` : `${Math.abs(homeTeam.stats.streak)}L`,
        pointDifferential: homeTeam.stats.pointsFor - homeTeam.stats.pointsAgainst,
        gamesPlayed: awayTeam.stats.games,
        playerStats: playedStats.filter((s) => s.teamID == game.homeTeamID)
      }
    }

    const regularSeasonGames = games.filter((g) => g.season == game.season && g.type == 'regular_season' && game.status !== 'cancelled')
    const regularSeasonTeams = seasonTeams

    const regularSeasonGamesAwayTeam = regularSeasonGames.filter((g) => g.homeTeamID == game.awayTeamID || g.awayTeamID == game.awayTeamID)
    const regularSeasonGamesHomeTeam = regularSeasonGames.filter((g) => g.homeTeamID == game.homeTeamID || g.awayTeamID == game.homeTeamID)

    const regularSeasonGamesPerTeam = 2 * regularSeasonGames.length / regularSeasonTeams.length

    article['regularSeasonGamesPerTeam'] = regularSeasonGamesPerTeam
    article['teamDataAway']['gamesRemaining'] = regularSeasonGamesAwayTeam - awayTeam.stats.games
    article['teamDataHome']['gamesRemaining'] = regularSeasonGamesHomeTeam - homeTeam.stats.games

    if (game.type == 'playoff') {
      const playoffRoundMap = {
        'first-round': "First Round",
        'conference-semifinal': "Conference Semifinal",
        'conference-final': "Conference Final",
        'final': "Championship"
      }
      article['playoffRound'] = playoffRoundMap[game.playoffTags[0]]
    }

    // Fetch 7 articles from games closest in date to this game, to avoid repeating title verbiage
    const recentArticlesSnap = await getDocs(collection(db, 'articles'));
    const recentTitles = recentArticlesSnap.docs
      .map(d => {
        const articleGame = utils.getGameForID(games, d.id);
        return { id: d.id, title: d.data().title, gameDate: articleGame?.date || null };
      })
      .filter(a => a.title && a.gameDate && a.id !== game.id)
      .sort((a, b) => Math.abs(a.gameDate - game.date) - Math.abs(b.gameDate - game.date))
      .slice(0, 7)
      .map(a => a.title);

    const res = await fetch('/.netlify/functions/create_game_article', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ article, season: SEASON, recentTitles, talkingPoints }),
    });

    if (!res.ok) {
      const error = await res.text();
      throw new Error(`Article generation failed: ${error}`);
    }

    const data = await res.json();

    const uniquePlayers = [];
    const seenPlayerIDs = new Set();
    for (const s of playedStats) {
      if (!seenPlayerIDs.has(s.playerID)) {
        seenPlayerIDs.add(s.playerID);
        uniquePlayers.push({ id: s.playerID, name: s.playerName });
      }
    }

    const docRef = doc(db, 'articles', game.id);
    await setDoc(docRef, {
      title: data.title,
      summary: data.summary,
      body: data.body,
      talkingPoints,
      game_id: game.id,
      created_at: Timestamp.fromDate(new Date()),
      awayTeamID: game.awayTeamID,
      awayTeamName: awayTeam.name,
      homeTeamID: game.homeTeamID,
      homeTeamName: homeTeam.name,
      players: uniquePlayers,
    });

    await updateArticleMetadataInUpcomingGames(game.season);
  }

  function getAverageStatsForPlayer (playerStats) {
    // each stat in stats corressponds to a player we need to update

    let playedPlayerStats = playerStats.filter((s) => s.status == 'played')
    let playedPlayerStatsWithBlocksAndSteals = playedPlayerStats.filter((s) => utils.getGameForID(games, s.gameID).date > utils.DATE_WHEN_BLOCKS_AND_STEALS_WERE_ADDED)

    let gamesPlayed = playedPlayerStats.length
    let gamesPlayedWithBlocksAndSteals = playedPlayerStatsWithBlocksAndSteals.length
    let totalPoints = playedPlayerStats.reduce((partialPts, stats) => partialPts + stats.points, 0)
    let totalRebounds = playedPlayerStats.reduce((partialRbs, stats) => partialRbs + stats.rebounds, 0)
    let totalAssists = playedPlayerStats.reduce((partialAst, stats) => partialAst + stats.assists, 0)
    let totalSteals = playedPlayerStats.reduce((partialAst, stats) => partialAst + (stats.steals || 0), 0)
    let totalBlocks = playedPlayerStats.reduce((partialAst, stats) => partialAst + (stats.blocks || 0), 0)

    let avgPoints = '0', avgRebounds = '0', avgAssists = '0', avgBlocks = '0', avgSteals = '0';
    if (gamesPlayed > 0) {
      avgPoints = (totalPoints / gamesPlayed).toFixed(1)
      avgRebounds = (totalRebounds / gamesPlayed).toFixed(1)
      avgAssists = (totalAssists / gamesPlayed).toFixed(1)
    }
    if (gamesPlayedWithBlocksAndSteals > 0) {
      avgSteals = (totalSteals / gamesPlayedWithBlocksAndSteals).toFixed(1)
      avgBlocks = (totalBlocks / gamesPlayedWithBlocksAndSteals).toFixed(1)
    }

    const statTeamsID = [...new Set(playerStats.map(s => s.teamID))];

    let statTeams = statTeamsID.map(id => {
      const team = utils.getTeamForID(teams, id);

      return {
        id,
        name: team.name,
        nameAbbv: team.nameAbbv
      };
    });

    let wins = 0;
    let losses = 0
    let regularSeasonWins = 0;
    let regularSeasonLosses = 0;
    let playoffWins = 0;
    let playoffLosses = 0;

    playedPlayerStats.forEach((s) => {
      const g = utils.getGameForID(games, s.gameID);
      if (!g) return;

      const isWin = g.winner == s.teamID;
      if (isWin) {
        wins++;

        if (g.type === "regular_season") {
          regularSeasonWins++;
        }

        if (g.type === "playoff") {
          playoffWins++;
        }
      } else {
        losses++;

        if (g.type === "regular_season") {
          regularSeasonLosses++;
        }

        if (g.type === "playoff") {
          playoffLosses++;
        }
      }
    });

    return {
      assists: avgAssists,
      blocks: avgBlocks,
      points: avgPoints,
      rebounds: avgRebounds,
      steals: avgSteals,
      gamesPlayed: gamesPlayed,
      teams: statTeams,
      wins: wins,
      regularSeasonWins: regularSeasonWins,
      playoffWins: playoffWins,
      losses: losses,
      regularSeasonLosses: regularSeasonLosses,
      playoffLosses: playoffLosses
    }
  }

  function getPlayerViewStatsForGames (playerSeasonStats, gamesOverride) {
    const gamesList = gamesOverride || games;
    let playerViewStats = []

    for (const stat of playerSeasonStats) {
      let game = utils.getGameForID(gamesList, stat.gameID)
      if (!game) continue;

      const homeTeamData = utils.getTeamForID(seasonTeams, game.homeTeamID);
      const awayTeamData = utils.getTeamForID(seasonTeams, game.awayTeamID);

      let homeTeam = {
        id: game.homeTeamID,
        name: homeTeamData?.name || '',
        nameAbbv: homeTeamData?.nameAbbv || ''
      }
      let awayTeam = {
        id: game.awayTeamID,
        name: awayTeamData?.name || '',
        nameAbbv: awayTeamData?.nameAbbv || ''
      }

      playerViewStats.push({
        assists: stat.assists,
        blocks: stat.blocks,
        points: stat.points,
        rebounds: stat.rebounds,
        steals: stat.steals,
        gameID: stat.gameID,
        team: homeTeam.id == stat.teamID ? homeTeam : awayTeam,
        opponent: homeTeam.id == stat.teamID ? awayTeam : homeTeam,
        result: game.winner == stat.teamID ? 'W' : 'L',
        score: homeTeam.id == stat.teamID ? `${game.score.home} - ${game.score.away}` : `${game.score.away} - ${game.score.home}`,
        status: stat.status,
        date: game.date
      })
    }

    // sort from oldest to most recent game
    playerViewStats.sort((a, b) => new Date(a.date) - new Date(b.date));

    return playerViewStats
  }

  const savePlayerViews = async (game, stats, allGameStats) => {
    try {
    // games is React state — its closure value is stale because setGames is
    // async. Build a local merged copy that includes the current game's data
    // so W/L records are correct.
    const gamesWithCurrent = games.map(g => g.id === game.id ? game : g);

    for (const stat of stats) {
      let playerCareerStats = allGameStats.filter((s) => s.playerID == stat.playerID)
      let playerSeasonStats = playerCareerStats.filter((s) => {
        const g = utils.getGameForID(gamesWithCurrent, s.gameID);
        return g && g.season == game.season;
      })

      let seasonStatAverages = getAverageStatsForPlayer(playerSeasonStats)
      let careerStatAverages = getAverageStatsForPlayer(playerCareerStats)

      let seasonAverages = {}
      seasonAverages[game.season] = {
        assists: seasonStatAverages.assists,
        blocks: seasonStatAverages.blocks,
        points: seasonStatAverages.points,
        rebounds: seasonStatAverages.rebounds,
        steals: seasonStatAverages.steals,
        gamesPlayed: seasonStatAverages.gamesPlayed,
        teams: seasonStatAverages.teams
      }
      seasonAverages['all'] = {
        assists: careerStatAverages.assists,
        blocks: careerStatAverages.blocks,
        points: careerStatAverages.points,
        rebounds: careerStatAverages.rebounds,
        steals: careerStatAverages.steals,
        gamesPlayed: careerStatAverages.gamesPlayed,
        teams: careerStatAverages.teams
      }

      let career = {
        wins: careerStatAverages.wins,
        regularSeasonWins: careerStatAverages.regularSeasonWins,
        playoffWins: careerStatAverages.playoffWins,
        losses: careerStatAverages.losses,
        regularSeasonLosses: careerStatAverages.regularSeasonLosses,
        playoffLosses: careerStatAverages.playoffLosses
      }

      let seasonStats = getPlayerViewStatsForGames(playerSeasonStats, gamesWithCurrent)

      await setDoc(doc(db, "playerViews", stat.playerID), {
        seasonAverages: {
          [game.season]: seasonAverages[game.season],
          all: seasonAverages['all']
        },
        seasonStats: {
          [game.season]: seasonStats
        },
        career: career
      }, { merge: true });
    }
    } catch (error) {
      console.error('Error saving player views:', error);
    }
  }

  const saveStatsForGame = async (game, stats) => {
    try {
      let gameStatRef = doc(db, 'gameStats', game.id);
      await setDoc(gameStatRef, { 'stats': stats });
    } catch (error) {
      console.error('Error saving stat:', error);
    }
  }

  const updateRecords = async (game, stats) => {
    const playedStats = stats.filter(s => s.status === 'played');
    if (playedStats.length === 0) return;

    const statKeys = ['points', 'rebounds', 'assists', 'steals', 'blocks'];

    const [leagueSnap, seasonSnap] = await Promise.all([
      getDoc(doc(db, 'records', 'league')),
      getDoc(doc(db, 'records', `season_${game.season}`))
    ]);

    const leagueRec = leagueSnap.exists() ? leagueSnap.data() : {};
    const seasonRec = seasonSnap.exists() ? seasonSnap.data() : {};
    const newLeague = { ...leagueRec };
    const newSeason = { ...seasonRec };
    let leagueUpdated = false;
    let seasonUpdated = false;

    statKeys.forEach(key => {
      const gameMax = Math.max(...playedStats.map(s => s[key] || 0));
      const newHolders = playedStats
        .filter(s => (s[key] || 0) === gameMax)
        .map(s => ({ playerID: s.playerID, gameID: game.id }));

      const leagueRecord = leagueRec[key] || 0;
      if (gameMax > leagueRecord) {
        newLeague[key] = gameMax;
        newLeague[`${key}_holders`] = newHolders;
        leagueUpdated = true;
      } else if (gameMax === leagueRecord && gameMax > 0) {
        const existing = leagueRec[`${key}_holders`] || [];
        const merged = [...existing];
        newHolders.forEach(h => {
          if (!merged.find(e => e.playerID === h.playerID && e.gameID === h.gameID)) {
            merged.push(h);
            leagueUpdated = true;
          }
        });
        if (leagueUpdated) newLeague[`${key}_holders`] = merged;
      }

      const seasonRecord = seasonRec[key] || 0;
      if (gameMax > seasonRecord) {
        newSeason[key] = gameMax;
        newSeason[`${key}_holders`] = newHolders;
        seasonUpdated = true;
      } else if (gameMax === seasonRecord && gameMax > 0) {
        const existing = seasonRec[`${key}_holders`] || [];
        const merged = [...existing];
        newHolders.forEach(h => {
          if (!merged.find(e => e.playerID === h.playerID && e.gameID === h.gameID)) {
            merged.push(h);
            seasonUpdated = true;
          }
        });
        if (seasonUpdated) newSeason[`${key}_holders`] = merged;
      }
    });

    const writes = [];
    if (leagueUpdated) writes.push(setDoc(doc(db, 'records', 'league'), newLeague));
    if (seasonUpdated) writes.push(setDoc(doc(db, 'records', `season_${game.season}`), newSeason));

    // Update per-player career records
    const playerIDs = [...new Set(playedStats.map(s => s.playerID))];
    const careerSnaps = await Promise.all(playerIDs.map(pid => getDoc(doc(db, 'records', pid))));
    playerIDs.forEach((pid, i) => {
      const careerRec = careerSnaps[i].exists() ? { ...careerSnaps[i].data() } : {};
      let playerUpdated = false;
      statKeys.forEach(key => {
        const gameVal = playedStats.filter(s => s.playerID === pid).reduce((max, s) => Math.max(max, s[key] || 0), 0);
        if (gameVal > (careerRec[key] || 0)) {
          careerRec[key] = gameVal;
          playerUpdated = true;
        }
      });
      if (playerUpdated) writes.push(setDoc(doc(db, 'records', pid), careerRec));
    });

    if (writes.length > 0) await Promise.all(writes);
  }

  const saveSeasonUpcomingGames = async (gamesOverride) => {
    try {
      const seasonRef = doc(db, 'seasons', String(game.season));
      const upcomingGames = utils.getUpcomingGames(gamesOverride || games, String(game.season))

      await updateDoc(seasonRef, {
        upcomingGames: upcomingGames,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error saving season upcoming games:', error);
    }
  }

  const saveSeasonTeams = async ()=> {
    try {
      const seasonId = String(game.season);
      const seasonRef = doc(db, 'seasons', seasonId);

      const sznTeams = utils.getSeasonTeams(games, seasonTeams, seasonId)

      await updateDoc(seasonRef, {
        teams: sznTeams,
        updatedAt: serverTimestamp()
      });

      const updates = sznTeams.map((team) => {
        const seasonTeamId = `${team.id}-${seasonId}`;
        const seasonTeamRef = doc(db, 'seasonTeams', seasonTeamId);

        return updateDoc(seasonTeamRef, {
          seed: team.seed,
        });
      });
      await Promise.all(updates);
    } catch (error) {
      console.error('Error saving season teams:', error);
    }
  }

  const saveSeasonLeaders = async (allGameStats) => {
    try {
      const seasonRef = doc(db, 'seasons', String(game.season));

      const leaderCount = 10;
      const leaders = utils.getSeasonLeaders(games, seasonTeams, players, allGameStats, String(game.season), leaderCount)

      await updateDoc(seasonRef, {
        leaders: leaders,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error saving season leaders:', error);
    }
  }

  const updateGame = async (game) => {
    try {
      const gameRef = doc(db, 'games', game.id);

      await updateDoc(gameRef, {
        homeTeamID: game.homeTeamID,
        awayTeamID: game.awayTeamID,
      });

    } catch (error) {
      console.error('Error updating game teams:', error);
    }
  };


  const saveGame = async (game) => {
    try {
      const gameRef = doc(db, 'games', game.id);
      const gameSnap = await getDoc(gameRef);

      const data = {
        score: {
          home: game.score.home,
          away: game.score.away
        },
        winner: game.winner,
        status: game.status,
        overtime: game.overtime
      };

      if (gameSnap.exists()) {
        await updateDoc(gameRef, data);
      } else {
        data['awayTeamID'] = game.awayTeamID
        data['date'] = game.date
        data['homeTeamID'] = game.homeTeamID
        data['id'] = game.id
        data['referees'] = []
        data['season'] = game.season
        data['type'] = game.type
        data['overtime'] = game.overtime

        await setDoc(gameRef, data, { merge: true });
      }
    } catch (error) {
      console.error('Error saving game:', error);
    }
  }

  function getIDForPlayerAndStat(player, stat) {
    return `${player.id}-${stat}`
  }

  function noStatsGame () {
    // abstract this
    let statsForThisGame = gameStats[gameID] || [];
    let statStatuses = statsForThisGame.map((stat) => stat.status)

    return statStatuses.includes('no-stats');
  }

  function forfeitedGame () {
    // abstract this
    let statsForThisGame = gameStats[gameID] || [];
    let statStatuses = statsForThisGame.map((stat) => stat.status)

    return statStatuses.includes('forfeit');
  }

  function overtimeGame () {
    if (game?.overtime == true) {
      return true
    }
    
    return false
  }

  function displayEditGame () {
    let game = utils.getGameForID(games, gameID);

    if (game.date < utils.DATE_WHEN_BLOCKS_AND_STEALS_WERE_ADDED) {
      SAVE_BLOCKS_AND_STEALS = false;
    }
    let awayTeam = utils.getTeamForID(seasonTeams, game.awayTeamID);
    let homeTeam = utils.getTeamForID(seasonTeams, game.homeTeamID);
    return (
      <fieldset disabled={isSaving}>
        <div>
            <form className='league-game-edit'>
              <div className='league-game-edit-scores'>
                <div className='text-left admin-edit-col'>
                  <p><b>{ awayTeam.name.toUpperCase() }</b></p>
                  <div className="mb-3">
                    <label className="form-label">Final Score</label>
                    <input type="number" className="form-control" id="finalScoreAwayTeam" defaultValue={ game.status == 'completed' ? game.score.away : '' }></input>
                  </div>
                </div>

                <div className='text-right admin-edit-col'>
                  <p><b>{ homeTeam.name.toUpperCase() }</b></p>
                  <div className="mb-3">
                    <label className="form-label">Final Score</label>
                    <input type="number" className="form-control" id="finalScoreHomeTeam" defaultValue={ game.status == 'completed' ? game.score.home : '' }></input>
                  </div>
                </div>
              </div>

              <div className='league-game-edit-checkboxes'>
                <div className='mb-3 admin-edit-col centered-edit-col'>
                  <label className="form-check-label">Rewrite Article?</label>
                  <input className="form-check-input big-checkbox" type="checkbox" value="" id="rewriteArticleCheckbox" defaultChecked={ false }/>
                </div>

                <div className='mb-3 admin-edit-col centered-edit-col'>
                  <label className="form-check-label">No Stats?</label>
                  <input className="form-check-input big-checkbox" type="checkbox" value="" id="noStatsCheckbox" defaultChecked={ noStatsGame() }/>
                </div>

                <div className='mb-3 admin-edit-col centered-edit-col'>
                  <label className="form-check-label">Forfeit?</label>
                  <input className="form-check-input big-checkbox" type="checkbox" value="" id="forfeitCheckbox" defaultChecked={ forfeitedGame() }/>
                </div>

                <div className='mb-3 admin-edit-col centered-edit-col'>
                  <label className="form-check-label">OT?</label>
                  <input className="form-check-input big-checkbox" type="checkbox" value="" id="overtimeCheckbox" defaultChecked={ overtimeGame() }/>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Talking Points / Storylines <span style={{ fontWeight: 400, color: '#888' }}>(optional — used by AI for article)</span></label>
                <textarea className="form-control" id="articleTalkingPoints" rows={3} defaultValue={savedTalkingPoints} />
              </div>
            </form>

            <div className="league-table-container">
              <table className="table league-table">
              <thead className="league-table-heading-hole">
                  <tr>
                    <th className="league-table-heading-name"><span>Player</span></th>
                    <th className="league-table-heading hole"><span>PTS</span></th>
                    <th className="league-table-heading hole"><span>REB</span></th>
                    <th className="league-table-heading hole"><span>AST</span></th>
                    { SAVE_BLOCKS_AND_STEALS == false ? '' :
                      <>
                      <th className="league-table-heading hole"><span>STL</span></th>
                      <th className="league-table-heading hole"><span>BLK</span></th>
                      </>
                    }
                  </tr>
                </thead>
                <thead className="league-table-heading-total">
                <tr>
                    <th className="league-table-heading-name"><span>Player</span></th>
                    <th className="league-table-heading hole"><span>pts</span></th>
                    <th className="league-table-heading hole"><span>reb</span></th>
                    <th className="league-table-heading hole"><span>ast</span></th>
                    { SAVE_BLOCKS_AND_STEALS == false ? '' :
                      <>
                      <th className="league-table-heading hole"><span>stl</span></th>
                      <th className="league-table-heading hole"><span>blk</span></th>
                      </>
                    }
                  </tr>
                </thead>
                <tbody>
                  { displayPlayerInputRows(game, awayTeam, homeTeam) }
                </tbody>
              </table>
            </div>
          </div>
        <div className='league-game-save-button text-align-center'>
          <button type="button" className="btn btn-dark" onClick={saveGameData} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
        {isSaving && (
          <div className="admin-save-progress">
            <div className="progress admin-save-progress-bar">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated bg-dark"
                role="progressbar"
                style={{ width: `${saveProgress}%` }}
                aria-valuenow={saveProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              />
            </div>
            <p className="admin-save-step-label">{saveStep}</p>
          </div>
        )}
        <div className='league-game-erase-button text-align-center'>
          <button type="button" className="btn btn-danger" disabled={isSaving}>Erase</button>
        </div>
      </fieldset>
    )
  }

  if (dataReady == false || currentUser == null) {
    return (
      <div className="Schedule">
        <LoadingAnimation />
      </div>
    )
  }
  else {
    return (
      <>
      <div className="Schedule">
        <div className="league-content">

          <div className="league-body">
            <p className='league-admin-back-button'><a className='league-link' href="/admin"><b>← Back to Admin Home</b></a></p>
            { gameID ? <p className='league-admin-back-button'><a className='league-link' href="/admin/games"><b>← Back to Admin &gt; Games</b></a></p> : '' }

            <p className="league-title-standard-secondary text-align-center">Edit Game</p>
            <div className="dropdown league-dropdown">
            </div>
            <div className="league-table-container schedule">
              {
                gameID ?
                displayEditGame()
                :
                displayGames()
              }
            </div>
          </div>

        </div>

        <Footer/>
      </div>

      {showArticleModal && (
        <div className="article-modal-overlay">
          <div className="article-modal">
            <p className="article-modal-title">
              {articleStatus === 'done' ? 'Article Ready' : articleStatus === 'error' ? 'Article Failed' : 'Generating Article…'}
            </p>

            {articleStatus === 'generating' && (
              <>
                <div className="progress article-modal-progress">
                  <div className="progress-bar progress-bar-striped progress-bar-animated bg-dark" role="progressbar" style={{ width: '100%' }} />
                </div>
                <p className="article-modal-note">Game data has been saved. You can leave this page and the article will still be generated and saved in the background.</p>
              </>
            )}

            {articleStatus === 'done' && (
              <p className="article-modal-note">The article has been generated and saved.</p>
            )}

            {articleStatus === 'error' && (
              <p className="article-modal-note">Something went wrong generating the article. Game data was saved successfully.</p>
            )}

            <button className="btn btn-dark article-modal-btn" onClick={() => navigate(`/games/${gameID}`)}>
              Back to Box Score
            </button>
          </div>
        </div>
      )}
      </>
    );
  }
}

export default AdminGames;
